import { CustomFieldOption, CustomFieldType } from "src/api/contractGroup/contractgroup-type";
import { Sort } from "../public-types";

/**
 * 커스텀 필드 대상 타입
 * - 계약그룹과 동일한 custom-fields 리소스를 targetType 으로 스코프 구분
 */
export const CUSTOM_FIELD_TARGET_TYPE = {
  PARTNER_MEMBER: "PARTNER_MEMBER", // 이용자(파트너 회원) 부가정보
} as const;
export type CustomFieldTargetType = (typeof CUSTOM_FIELD_TARGET_TYPE)[keyof typeof CUSTOM_FIELD_TARGET_TYPE];

/**
 * 이용자 부가정보 커스텀 필드 정의 (targetType=PARTNER_MEMBER)
 * 계약그룹 CustomFieldModel 을 복제 후 확장 (isAppVisible / isAppEditable 추가, isRequired / groupIdList 제거)
 */
export type PartnerMemberCustomFieldModel = {
  fieldId: number; // 필드 ID (신규 등록 시 0, 수정 시 초회 fieldId 재전달)
  partnerId?: string; // 파트너 ID
  fieldName?: string; // 필드명
  slug?: string; // 물리명 (ESL 연동용 slug) - 선택 입력, 영문/숫자/_ 50자, 파트너·대상 타입 단위 중복 불가(eCT121)
  fieldType?: CustomFieldType; // 필드 유형 (RADIO / CHECKBOX / TEXT / NUMBER / CALENDAR / URL)
  orderNum?: number; // 정렬 순번 (사용앱 · Ctrl.R 출력 순서)
  isVisible?: boolean; // Ctrl.Room 노출 여부 (default = false)
  isAppVisible?: boolean; // 사용앱 노출 여부 (default = false) - isVisible=true 여야 true 가능
  isAppEditable?: boolean; // 사용앱 수정 여부 (default = false) - isAppVisible=true 여야 true 가능
  isDeleted?: boolean; // 삭제 여부 (default = false)
  createdDate?: string; // 생성일
  createdBy?: string; // 생성자
  modifiedDate?: string; // 수정일
  modifiedBy?: string; // 수정자
  optionList?: CustomFieldOption[]; // 커스텀 필드 옵션 리스트 (RADIO / CHECKBOX)
};

/**
 * 커스텀필드 스코프(대상×용도) 설정
 * - 사용앱(STPM) 홈에 '근무정보' 진입 아이콘을 출력할지 여부
 * - 아이콘 명 입력 기능은 이번 범위 제외 (이미지+텍스트 일체형)
 */
export type PartnerMemberCustomFieldSetting = {
  isAppVisible: boolean; // 아이콘 출력 여부 (저장된 설정이 없으면 false)
};

/** RCR11 응답 data / RCR12 요청 body 공통 구조 */
export type PartnerMemberCustomFieldScope = {
  targetType?: CustomFieldTargetType;
  setting?: PartnerMemberCustomFieldSetting;
  customFieldList?: PartnerMemberCustomFieldModel[];
};

/** 노출/사용앱 노출 최대 개수 (커스텀 필드 등록 상한과 동일 - 등록한 필드를 모두 노출 가능) */
export const CUSTOM_FIELD_VISIBLE_MAX = 10;

// ---- 이용자 부가정보 목록(RCR31) 관련 타입 ----

// 이용자 부가정보 목록 검색 파라미터 (RCR31)
export interface PartnerMemberListParam {
  page?: number;
  size?: number;
  sort?: Sort;
  searchType?: string; // MEMBER_NO / PHONE_NUMBER / EMAIL / NICKNAME / CONTRACT_MANAGE_ID / ALL
  searchValue?: string;
  fieldId?: number; // 커스텀필드 검색 - 필드 ID
  inputValue?: string; // 커스텀필드 검색 - 텍스트류(정확 일치)
  optionIdList?: string; // 커스텀필드 검색 - 선택형 옵션 ID
}

// 신청계약 요약 (계약관리 단위, 복수)
export interface PartnerMemberContractSummary {
  contractManageId?: number; // 계약관리 ID
  contractApplyNumber?: string; // 이용신청 번호 (신청계약번호)
  contractName?: string; // 계약명 (계약명(필수))
  contractNickname?: string; // 계약 닉네임 (계약명(선택))
}

// 커스텀필드 입력값 (이용자 부가정보) - inputList 요소
export interface PartnerMemberCustomFieldInput {
  fieldId: number; // 필드 ID (커스텀 필드 정의와 매칭)
  inputValue?: string; // 입력값 (텍스트/숫자/캘린더/URL)
  isDeleted?: boolean;
  optionList?: CustomFieldOption[]; // 선택값 (단일/다중선택)
}

// 이용자 부가정보 목록 행 (RCR31 data.content[])
export interface PartnerMemberListModel {
  partnerMemberId: number; // 파트너회원 ID
  memberNo: string; // 회원번호
  phoneNumber?: string; // 휴대폰번호
  email?: string; // 이메일
  nickname?: string; // 닉네임
  secondaryNickname?: string; // 닉네임(추가)
  createdDate?: string; // 생성일
  modifiedDate?: string; // 수정일
  contractSummaryList?: PartnerMemberContractSummary[]; // 신청계약 요약 리스트
  inputList?: PartnerMemberCustomFieldInput[]; // 커스텀필드 입력값 리스트 (노출 필드만)
}

// ---- 이용자 부가정보 상세(RCR32) / 저장(RCR33) 관련 타입 ----

// 회원정보 컨테이너 (RCR32 data.member) - 수정 불가
export interface PartnerMemberInfo {
  memberNo: string; // 회원번호
  phoneNumber?: string; // 휴대폰번호
  email?: string; // 이메일
  nickname?: string; // 닉네임
  secondaryNickname?: string; // 닉네임(추가)
  isPhoneNumberVerified?: boolean; // 휴대폰번호 인증여부 (인증 시 체크 아이콘 표기)
  isEmailVerified?: boolean; // 이메일 인증여부 (인증 시 체크 아이콘 표기)
}

// 이용자 부가정보 상세 응답 (RCR32 data)
export interface PartnerMemberExtraInfo {
  member: PartnerMemberInfo;
  inputList?: PartnerMemberCustomFieldInput[]; // 노출 필드(isVisible=true) 입력값
}

// 부가정보 저장 요청 input (RCR33 inputList[])
export interface PartnerMemberInputSaveItem {
  fieldId: number;
  inputValue?: string; // 텍스트/숫자/캘린더/URL
  optionList?: { optionId: number }[]; // 단일/다중선택
  isDeleted?: boolean; // 삭제 시 true
}
